/**
 * Pastille de la barre e3 sur une landing ou une page dynamique : le choix part sur /marketplace filtrée.
 * Ici, l'état InstantSearch de la page est modifié avant d'être sérialisé (`uiStateToMarketplaceUrl`), et on
 * renvoie les facettes à retirer de l'URL même si la page les impose (le sérialiseur réinjecte les filtres
 * curés de la page : marques, modèles).
 *
 * - Marque choisie (autre que la sélection) : cette marque seule. Les marques imposées par la page
 *   (`refinementList[make]`, ex. Porsche et BMW) et tout ce qui dépend de la marque (modèles, options,
 *   couleurs, finition saisie) sont retirés : sinon « Audi » + « Porsche, BMW » ne donne aucun véhicule.
 * - Marque retirée (pastille active « Porsche ✕ ») : /marketplace sans cette marque ni ses modèles ; les
 *   autres marques imposées par la page restent. Explicite : la landing peut ne pas avoir la croix du filtre
 *   Marque (landing sans stock).
 * - Modèle ou carburant : ajouté ou retiré de la liste ; liste vide → la facette quitte l'URL. Un modèle
 *   est une famille (« Cayenne ») : toutes ses désignations sont ajoutées ou retirées ensemble.
 */

import { MAKE_DEPENDENT_REFINEMENT_FACETS, normalizeMakeSelection } from "../services/algoliaRouting.shared";
import { familyToggleValues } from "./model-families";

export interface ChipIndexState {
    menu?: Record<string, string>;
    refinementList?: Record<string, string[]>;
}

export interface ChipRedirectOmissions {
    /** Retirées du menu et de la liste (et de la racine, ex. `specification`). */
    omitFacets: string[];
    /** Retirées de `refinementList` seulement. */
    omitRefinementList: string[];
}

const MAKE_DEPENDENT = [...MAKE_DEPENDENT_REFINEMENT_FACETS, "specification"];

export function applyListingChipToUiState(
    indexState: ChipIndexState,
    attribute: "make" | "model" | "energy_type",
    value: string,
    /** Marques imposées par la page : la sélection courante quand l'état de la recherche ne les porte pas. */
    pageMakes: string[] = [],
): ChipRedirectOmissions {
    if (attribute === "make") {
        const fromState = normalizeMakeSelection(indexState);
        const current = fromState.length > 0 ? fromState : pageMakes;
        const removing = current.includes(value);
        if (indexState.refinementList) {
            delete indexState.refinementList.make;
            MAKE_DEPENDENT_REFINEMENT_FACETS.forEach((facet) => delete indexState.refinementList?.[facet]);
        }
        if (removing) {
            if (indexState.menu) delete indexState.menu.make;
            const rest = current.filter((make) => make !== value);
            if (rest.length === 0) return { omitFacets: ["make", ...MAKE_DEPENDENT], omitRefinementList: [] };
            // Page qui impose plusieurs marques : seule la marque touchée part, les autres restent
            indexState.refinementList = { ...(indexState.refinementList ?? {}), make: rest };
            return { omitFacets: [...MAKE_DEPENDENT], omitRefinementList: [] };
        }
        indexState.menu = { ...(indexState.menu ?? {}), make: value };
        return { omitFacets: [...MAKE_DEPENDENT], omitRefinementList: ["make"] };
    }

    const list = indexState.refinementList?.[attribute] ?? [];
    const toggled = attribute === "model" ? familyToggleValues(value, list) : [value];
    const removing = toggled.some((item) => list.includes(item));
    const next = removing
        ? list.filter((item) => !toggled.includes(item))
        : [...list, ...toggled.filter((item) => !list.includes(item))];
    indexState.refinementList = { ...(indexState.refinementList ?? {}), [attribute]: next };

    return next.length === 0
        ? { omitFacets: [], omitRefinementList: [attribute] }
        : { omitFacets: [], omitRefinementList: [] };
}
